import { useRef, useState, useCallback, useMemo } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  useDismiss,
  useInteractions,
  FloatingContext,
  Placement
} from '@floating-ui/react';

// Image alignment options for the toolbar
export type ImageAlignment = 'left' | 'center' | 'right' | 'full' | 'auto';

// Hook configuration options
interface UseImageToolbarOptions {
  placement?: Placement;
  offsetDistance?: number;
  shiftPadding?: number;
}

// Hook return type
export interface UseImageToolbarReturn {
  // Floating UI refs and styles
  refs: {
    setReference: (node: HTMLElement | null) => void;
    setFloating: (node: HTMLElement | null) => void;
  };
  floatingStyles: React.CSSProperties;
  // Arrow ref - SVGSVGElement for FloatingArrow component
  arrowRef: React.RefObject<SVGSVGElement>;
  // Selected image state
  selectedImage: HTMLImageElement | null;
  // Floating UI context for FloatingPortal and interactions
  context: FloatingContext;
  // Props getters for floating element
  getFloatingProps: () => Record<string, unknown>;
  // Actions
  selectImage: (img: HTMLImageElement) => void;
  deselectImage: () => void;
  // Open state
  isOpen: boolean;
}

/**
 * Custom hook for managing image toolbar positioning using Floating UI.
 * Provides auto-positioning, flip/shift behavior, ESC dismiss, and click-outside handling.
 */
export const useImageToolbar = (
  options: UseImageToolbarOptions = {}
): UseImageToolbarReturn => {
  const {
    placement = 'top',
    offsetDistance = 10,
    shiftPadding = 8
  } = options;

  // State for selected image
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);

  // Arrow element ref - SVGSVGElement type for FloatingArrow component
  const arrowRef = useRef<SVGSVGElement>(null);

  // Computed open state
  const isOpen = selectedImage !== null;

  // Deselect image handler - removes selection styling and clears state
  // Using functional update to avoid stale closure issues
  const deselectImage = useCallback(() => {
    setSelectedImage((current) => {
      if (current) {
        current.classList.remove('img-selected');
      }
      return null;
    });
  }, []);

  // Memoized onOpenChange handler to prevent recreation on every render
  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      deselectImage();
    }
  }, [deselectImage]);

  // Floating UI setup with middleware stack
  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: handleOpenChange,
    middleware: [
      offset(offsetDistance),
      flip({
        fallbackPlacements: ['bottom', 'left', 'right'],
        fallbackAxisSideDirection: 'start'
      }),
      shift({ padding: shiftPadding }),
      arrow({ element: arrowRef })
    ],
    whileElementsMounted: autoUpdate
  });

  // Setup dismiss behavior (ESC key and click outside)
  // referencePress: true prevents dismiss when clicking the reference element (the image)
  // This avoids the issue where the opening click is detected as "outside click"
  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: true,
    referencePress: true
  });

  // Combine all interactions
  const { getFloatingProps } = useInteractions([dismiss]);

  // Select image handler - adds selection styling and sets reference
  // Using functional update to handle previous image cleanup without stale closure
  const selectImage = useCallback((img: HTMLImageElement) => {
    // Add selection styling to new image
    img.classList.add('img-selected');

    // Set as floating reference
    refs.setReference(img);

    // Update state with functional update to handle previous image
    setSelectedImage((prevImage) => {
      // Deselect previous image if different
      if (prevImage && prevImage !== img) {
        prevImage.classList.remove('img-selected');
      }
      return img;
    });
  }, [refs]);

  // Memoize return value for performance
  return useMemo(() => ({
    refs,
    floatingStyles,
    arrowRef,
    selectedImage,
    context,
    getFloatingProps,
    selectImage,
    deselectImage,
    isOpen
  }), [
    refs,
    floatingStyles,
    selectedImage,
    context,
    getFloatingProps,
    selectImage,
    deselectImage,
    isOpen
  ]);
};

export default useImageToolbar;

